/**
 * Settings API — read/write settings from the Supabase key-value store.
 *
 * The `settings` table is a key-value store with `key` (text) and `value` (text)
 * columns. Each setting is stored as a separate row, e.g.:
 *   { key: 'bonus_bronze_amount', value: '1000' }
 *
 * Both the adviser and assistant read from this table so they share the same config.
 */

import supabase from './supabaseClient';

// Default settings used when no rows exist or keys are missing
const DEFAULTS = {
  payeAmount: 0,
  slaThresholdDays: 3,
  bronzeTarget: 20,
  silverTarget: 25,
  goldTarget: 30,
  bronzeAmount: 1000,
  silverAmount: 0,
  goldAmount: 0,
};

// Maps Supabase key names to camelCase settings field names
const KEY_TO_CAMEL = {
  paye_equivalent: 'payeAmount',
  sla_threshold_days: 'slaThresholdDays',
  bonus_bronze_count: 'bronzeTarget',
  bonus_silver_count: 'silverTarget',
  bonus_gold_count: 'goldTarget',
  bonus_bronze_amount: 'bronzeAmount',
  bonus_silver_amount: 'silverAmount',
  bonus_gold_amount: 'goldAmount',
};

// Reverse mapping: camelCase → Supabase key
const CAMEL_TO_KEY = Object.fromEntries(
  Object.entries(KEY_TO_CAMEL).map(([k, v]) => [v, k])
);

/**
 * Fetch all settings from Supabase and return as a camelCase object.
 * Missing keys are filled in with defaults.
 */
export async function getSettings() {
  try {
    const { data, error } = await supabase
      .from('settings')
      .select('key, value');

    if (error) {
      console.warn('[Settings] Failed to fetch from Supabase, using defaults:', error.message);
      return { ...DEFAULTS };
    }

    // Build settings object from key-value rows
    const settings = { ...DEFAULTS };
    for (const row of (data || [])) {
      const camelKey = KEY_TO_CAMEL[row.key];
      if (camelKey) {
        settings[camelKey] = parseFloat(row.value) || 0;
      }
    }

    return settings;
  } catch (err) {
    console.warn('[Settings] Error fetching settings:', err);
    return { ...DEFAULTS };
  }
}

/**
 * Save all settings to Supabase. Each setting is upserted as a key-value row.
 * Uses onConflict on the key column to update existing rows or insert new ones.
 */
export async function saveSettings(settings) {
  // Build an array of key-value rows to upsert
  const rows = Object.entries(CAMEL_TO_KEY).map(([camelKey, dbKey]) => ({
    key: dbKey,
    value: String(settings[camelKey] ?? DEFAULTS[camelKey] ?? 0),
  }));

  const { error } = await supabase
    .from('settings')
    .upsert(rows, { onConflict: 'key' });

  if (error) {
    throw new Error(`Failed to save settings: ${error.message}`);
  }

  // Return the saved values as a camelCase object
  return { ...DEFAULTS, ...settings };
}
